// retention.js — Auditoria silenciosa de retenção (Sprint refinamento).
//
// Registra eventos anônimos que orientarão decisões futuras baseadas em uso
// real. NUNCA altera comportamento do app. NUNCA quebra a UX se o backend
// estiver indisponível (fire-and-forget).
//
// Uso:
//   import { recordEvent, useScrollDepth } from "../lib/retention";
//   recordEvent("click", "prayer-week-open");
//   useScrollDepth("home"); // dispara 25/50/75/100
//
// Contrato do backend: POST /api/analytics/retention com batch de até 50
// eventos {session_id, type, target, path, payload, client_ts}.

import { useEffect, useRef } from "react";
import { api } from "./api";

const SESSION_KEY = "cm_ret_session_v1";
const QUEUE_KEY = "cm_ret_queue_v1";
const FLUSH_DEBOUNCE_MS = 3000;
const MAX_BATCH = 50;

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function _sessionId() {
  try {
    let v = localStorage.getItem(SESSION_KEY);
    if (!v) {
      v = _uuid();
      localStorage.setItem(SESSION_KEY, v);
    }
    return v;
  } catch {
    return _uuid();
  }
}

function _readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function _writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_BATCH)));
  } catch {
    /* ignore */
  }
}

let _flushTimer = null;

function _scheduleFlush() {
  if (_flushTimer) window.clearTimeout(_flushTimer);
  _flushTimer = window.setTimeout(_flush, FLUSH_DEBOUNCE_MS);
}

async function _flush() {
  _flushTimer = null;
  const q = _readQueue();
  if (!q.length) return;
  const events = q.slice(0, MAX_BATCH);
  _writeQueue(q.slice(events.length)); // remove otimista
  try {
    await api.post("/analytics/retention", { events }, { timeout: 6000 });
  } catch {
    // Se falhar, reinjeta silenciosamente para tentar de novo depois.
    const remaining = _readQueue();
    _writeQueue([...events, ...remaining].slice(-MAX_BATCH));
  }
}

/**
 * Registra um evento de retenção. Fire-and-forget.
 * @param {'view'|'click'|'scroll'|'share'} type
 * @param {string} target ex: 'home', 'devocional-open', 'prayer-week-day-3'
 * @param {object} [payload] campos opcionais
 */
export function recordEvent(type, target, payload) {
  if (!type || !target) return;
  let userId = null;
  try {
    const cached = JSON.parse(localStorage.getItem("cm_user_v1") || "null");
    userId = cached?.id || null;
  } catch {
    /* ignore */
  }
  const event = {
    session_id: _sessionId(),
    user_id: userId,
    type: String(type),
    target: String(target).slice(0, 64),
    path: (typeof window !== "undefined" && window.location?.pathname) || null,
    payload: payload && typeof payload === "object" ? payload : null,
    client_ts: Date.now(),
  };
  const q = _readQueue();
  q.push(event);
  _writeQueue(q);
  _scheduleFlush();
}

/**
 * Hook que dispara eventos scroll:25/50/75/100 para uma rota/target.
 * Cada threshold dispara UMA VEZ por sessão + target.
 */
export function useScrollDepth(target) {
  const firedRef = useRef(new Set());

  useEffect(() => {
    if (!target || typeof window === "undefined") return;
    const key = `cm_ret_scroll_${target}`;
    let alreadyFired;
    try {
      alreadyFired = new Set(JSON.parse(sessionStorage.getItem(key) || "[]"));
    } catch {
      alreadyFired = new Set();
    }
    firedRef.current = alreadyFired;

    const onScroll = () => {
      const doc = document.documentElement;
      const total = Math.max(doc.scrollHeight - window.innerHeight, 1);
      const ratio = window.scrollY / total;
      const pct = Math.round(ratio * 100);
      for (const bucket of [25, 50, 75, 100]) {
        if (pct >= bucket && !firedRef.current.has(bucket)) {
          firedRef.current.add(bucket);
          try {
            sessionStorage.setItem(
              key,
              JSON.stringify(Array.from(firedRef.current)),
            );
          } catch {
            /* ignore */
          }
          recordEvent("scroll", target, { depth: bucket });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fire uma vez na entrada para pegar view-only sem scroll ainda
    recordEvent("view", target);
    return () => window.removeEventListener("scroll", onScroll);
  }, [target]);
}

// Flush restante ao fechar aba/página (best-effort via keepalive fetch).
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      const q = _readQueue();
      if (!q.length) return;
      try {
        const url =
          (process.env.REACT_APP_BACKEND_URL || "") + "/api/analytics/retention";
        const body = JSON.stringify({ events: q.slice(0, MAX_BATCH) });
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: "application/json" });
          if (navigator.sendBeacon(url, blob)) _writeQueue([]);
        }
      } catch {
        /* ignore */
      }
    }
  });
}
